import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useFormContext} from 'react-hook-form';
import {ArrowRight, Check, RotateCw} from 'lucide-react';
import {Button} from '@chekinapp/ui';
import {VERIFICATION_METHODS, type VerificationMethod} from '@chekin/api';
import {getPhoneNumberString} from '@chekin/core';
import {usePublishContactsVerificationState} from 'hooks/iv/useContactsVerificationStore';
import {
  CODE_LENGTH,
  CONTACTS_FORM_ID,
  ContactsEmailInput as EmailInput,
  ContactsForm,
  ContactsPhoneInput as PhoneInput,
  ContactsVerificationCodes,
  VerificationStep,
  maskEmail,
  maskPhone,
  useContactsCopy,
  useVerification,
  type ContactsFormTypes as FormTypes,
} from '@chekin/shared/guest-form';
import {useReservation} from 'context/reservation';
import {PageWrapper} from './components/PageWrapper';

type Props = {
  channel: VerificationMethod;
  currentStep: VerificationStep;
  contactIndex: number;
  contactCount: number;
  onGenerate: () => void;
  onVerify: (data: string, isVerified: boolean) => void | Promise<void>;
  onChangeContacts: () => void;
  onBack?: () => void;
};

function ChannelVerification({
  channel,
  currentStep,
  contactIndex,
  contactCount,
  onGenerate,
  onVerify,
  onChangeContacts,
  onBack,
}: Props) {
  const {t} = useTranslation();
  const {
    getValues,
    formState: {isValid},
  } = useFormContext<FormTypes>();
  const isEmail = channel === VERIFICATION_METHODS.email;
  const isCodeStep = currentStep === VerificationStep.CODES_VERIFICATION;

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [isCodeMismatch, setIsCodeMismatch] = useState(false);

  const {data: reservation} = useReservation();
  const {isLoading, contactsData, resendCode, verifyCode, onSubmit} = useVerification({
    type: channel,
    reservationId: reservation?.id,
    onGenerate,
    onVerify,
    isMock: import.meta.env.DEV,
  });

  useEffect(() => {
    setCode('');
    setCodeError('');
    setIsCodeMismatch(false);
  }, [currentStep]);

  usePublishContactsVerificationState({
    channel,
    step: isCodeStep ? 'code' : 'input',
    hasError: isCodeMismatch,
  });

  const maskedTarget = useMemo(
    () =>
      isEmail
        ? maskEmail(contactsData)
        : maskPhone(contactsData, getValues('phone')?.code),
    [isEmail, contactsData, getValues],
  );

  const copy = useContactsCopy({
    channel,
    step: currentStep,
    isPreviousVerified: contactIndex > 1,
    maskedTarget,
  });

  const stepLabel =
    contactCount > 1
      ? t('contact_of_contacts', {contact: contactIndex, contacts: contactCount})
      : undefined;

  const handleSubmit = (data: FormTypes) =>
    onSubmit((isEmail ? data?.email : getPhoneNumberString(data?.phone)) || '');

  const clearError = () => {
    setCodeError('');
    setIsCodeMismatch(false);
  };

  const handleVerify = async () => {
    clearError();
    try {
      await verifyCode(code);
    } catch {
      setCodeError(copy.codeMismatch);
      setIsCodeMismatch(true);
    }
  };

  const handleResend = async () => {
    clearError();
    try {
      await resendCode();
    } catch {
      setCodeError(t('failed_to_resend_code'));
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    clearError();
  };

  return (
    <PageWrapper
      title={copy.title}
      intro={copy.intro}
      stepLabel={stepLabel}
      onBack={onBack}
      dock={
        isCodeStep ? (
          <Button
            className="w-full max-w-[340px]"
            disabled={code.length !== CODE_LENGTH || isLoading}
            loading={isLoading}
            onClick={() => void handleVerify()}
          >
            {isCodeMismatch ? t('try_again') : copy.submitLabel}
            {isCodeMismatch ? (
              <RotateCw className="h-[18px] w-[18px]" />
            ) : (
              <Check className="h-[18px] w-[18px]" />
            )}
          </Button>
        ) : (
          <Button
            type="submit"
            form={CONTACTS_FORM_ID}
            className="w-full max-w-[340px]"
            disabled={!isValid || isLoading}
            loading={isLoading}
          >
            {copy.submitLabel}
            <ArrowRight className="h-[18px] w-[18px]" />
          </Button>
        )
      }
    >
      {isCodeStep ? (
        <ContactsVerificationCodes
          code={code}
          onCodeChange={handleCodeChange}
          errorMessage={codeError}
          isCodeMismatch={isCodeMismatch}
          note={copy.note}
          noteIcon={copy.noteIcon}
          changeLabel={copy.changeLabel}
          onResend={handleResend}
          onChangeContacts={onChangeContacts}
          isLoading={isLoading}
        />
      ) : (
        <ContactsForm note={copy.note} noteIcon={copy.noteIcon} onSubmit={handleSubmit}>
          {isEmail ? (
            <EmailInput name="email" label={t('email')} />
          ) : (
            <PhoneInput name="phone" label={t('phone_number')} />
          )}
        </ContactsForm>
      )}
    </PageWrapper>
  );
}

export {ChannelVerification};
